import React, { useState, useEffect } from 'react';
import Chart from 'chart.js/auto';
import RecurringTransactions from './RecurringTransactions';
import UpcomingBills from './UpcomingBills';

// Custom hook for data fetching
const useFetchData = (url, token, params = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshIndex, setRefreshIndex] = useState(0); // Used to trigger refetch

  const refetch = () => setRefreshIndex(prevIndex => prevIndex + 1);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams(params).toString();
        const fullUrl = queryParams ? `${url}?${queryParams}` : url;
        const response = await fetch(fullUrl, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch data from ${fullUrl}`);
        }
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchData();
    }
  }, [url, token, refreshIndex, params]); // Add params to dependencies

  return { data, loading, error, refetch };
};

function Dashboard({ token }) {
  const [projectionStartDate, setProjectionStartDate] = useState(new Date().toISOString().split('T')[0]); // Default to today
  const [projectionChart, setProjectionChart] = useState(null); // State to hold the chart instance

  const { data: dashboardData, loading: dashboardLoading, error: dashboardError } = useFetchData('/dashboard', token);
  const { data: recurringTransactionsData, loading: recurringTransactionsLoading, error: recurringTransactionsError, refetch: refetchRecurringTransactions } = useFetchData('/recurring_transactions', token);
  const { data: billsData, loading: billsLoading, error: billsError, refetch: refetchBills } = useFetchData('/bills', token);
  const { data: projectionsData, loading: projectionsLoading, error: projectionsError, refetch: refetchProjections } = useFetchData('/projections', token, { startDateParam: projectionStartDate });

  const [balances, setBalances] = useState([]);
  const [predictions, setPredictions] = useState({});
  const [recurringTransactions, setRecurringTransactions] = useState([]);
  const [bills, setBills] = useState([]);
  const [projections, setProjections] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (dashboardData) {
      setBalances(dashboardData.balances);
      setPredictions(dashboardData.predictions);
      const ctx = document.getElementById('spendingChart');
      if (ctx && !Chart.getChart('spendingChart')) { // Check if chart instance already exists
        new Chart(ctx, {
          type: 'pie',
          data: { labels: ['Groceries', 'Utilities'], datasets: [{ data: [300, 150] }] }
        });
      }
    }
    if (recurringTransactionsData) {
      setRecurringTransactions(recurringTransactionsData);
    }
    if (billsData) {
      setBills(billsData);
    }
    if (projectionsData) {
      setProjections(projectionsData);

      const ctx = document.getElementById('projectionChart');
      if (ctx) {
        // Destroy existing chart if it exists
        if (projectionChart) {
          projectionChart.destroy();
        }

        const labels = projectionsData.projected_balance.map(p => p.date);
        const data = projectionsData.projected_balance.map(p => p.balance);

        const newChart = new Chart(ctx, {
          type: 'line',
          data: {
            labels: labels,
            datasets: [{
              label: 'Projected Balance',
              data: data,
              fill: false,
              borderColor: 'rgb(75, 192, 192)',
              tension: 0.1
            }]
          },
          options: {
            scales: {
              y: {
                beginAtZero: true
              }
            }
          }
        });
        setProjectionChart(newChart);
      }
    }

    if (dashboardError || recurringTransactionsError || billsError || projectionsError) {
      setError(dashboardError || recurringTransactionsError || billsError || projectionsError);
    }

  }, [dashboardData, recurringTransactionsData, billsData, projectionsData, dashboardError, recurringTransactionsError, billsError, projectionsError, projectionChart]);
  
  const openTellerConnect = () => {
    const connect = new TellerConnect({
      applicationId: process.env.REACT_APP_TELLER_APPLICATION_ID,
      environment: 'sandbox',  // Change to 'production' for live
      onSuccess: (authorization) => {
        fetch('/accounts/add', {
          method: 'POST',
          body: JSON.stringify({ accessToken: authorization.accessToken }),
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
      },
      onFailure: (reason) => console.error('Link failed', reason),
    });
    connect.open();
  };

  if (dashboardLoading || recurringTransactionsLoading || billsLoading || projectionsLoading) return <div>Loading dashboard...</div>;
  if (error) return <div style={{ color: 'red' }}>Error: {error}</div>;

  return (
    <div>
      <h1>EdgeBudget Dashboard</h1>
      <button onClick={openTellerConnect}>Add Bank Account</button>
      <div>Aggregated Balance: ${balances.reduce((a, b) => a + b, 0).toFixed(2)}</div>
      {predictions.shortage && (
        <div style={{ color: 'red', fontWeight: 'bold' }}>
          Shortage Alert: {predictions.shortage}
        </div>
      )}
      {!predictions.shortage && (
        <div>On track</div>
      )}
      <canvas id="spendingChart"></canvas>
      <button onClick={() => fetch('/predictions/generate', { headers: { Authorization: `Bearer ${token}` } })}>Run AI Predictions</button>
      
      <RecurringTransactions
        token={token}
        recurringTransactions={recurringTransactions}
        onUpdate={() => {
          refetchRecurringTransactions();
          refetchProjections(); // Refetch projections when recurring transactions change
        }}
      />

      <UpcomingBills
        token={token}
        bills={bills}
        onUpdate={() => {
          refetchBills();
          refetchProjections(); // Refetch projections when bills change
        }}
      />

      {projections && (
        <div>
          <h2>Projections</h2>
          <div>
            Start Date for Projection: 
            <input 
              type="date" 
              value={projectionStartDate} 
              onChange={(e) => setProjectionStartDate(e.target.value)} 
            />
          </div>
          <p>Can you make it? {projections.can_i_make_it ? 'Yes' : 'No'}</p>
          <h3>Projected Balance</h3>
          <canvas id="projectionChart"></canvas>
          <ul>
            {projections.projected_balance.map(p => (
              <li key={p.date}>{p.date}: ${p.balance.toFixed(2)}</li>
            ))}
          </ul>
          <p>Advice: {projections.advice}</p>
        </div>
      )}

      {/* Shared access: Invite wife via email link to join household_id */}
    </div>
  );
}

export default Dashboard;